import { API } from '../../providers/ApiInterceptorProvider';
import { ICreateFirmDto, mapHasHppPermissionsToApi } from 'pages/SignupPage/dtos/firm.dto';
import { CreateAddress } from 'types/address';
import { PermissionType } from '../../enums';
import { ICreateFirmResponse, IUpdateUserAgreementAndPolicyDto, Referrer } from './types';

export type UserType = 'INTERNAL' | 'EXTERNAL';

export interface User {
  id: number;
  companyId: number;
  createdAt: Date;
  deletedAt: Date;
  updatedAt: Date;
  email: string;
  firstName: string;
  lastName: string;
  phoneNumber: string;
  type: UserType;
  roles: {
    role: {
      type: PermissionType;
      permissions: unknown[];
    };
  }[];
}

export const userApi = {
  updateUserAgreementAndPolicy: async (data: IUpdateUserAgreementAndPolicyDto) => {
    try {
      await API.patch(`/user/agreement-and-privacy`, data);
    } catch (e: unknown) {
      return;
    }
  },
  getUser: async (): Promise<User | null> => {
    try {
      const response = await API.get<User>(`/user/current`);

      return response.data;
    } catch (err: any) {
      throw new Error(err);
    }
  },
  createAssociatedCompany: async (data: any) => {
    try {
      // but also create the new firm from here, and link the user's referrer to it
      const user = await userApi.getUser();
      const userId = user?.id;

      if (!userId) {
        console.error('User ID not found while trying to create firm');
        return;
      }
      const referrer = (await API.get<Referrer>(`/monolith/referrers?userId=${userId}`)).data;
      if (!referrer || !referrer.id) {
        console.error(
          'Referrer ID not found while trying to create firm - creating the firm anyway but not associating it with a referrer',
        );
        return;
      }

      const firmData: ICreateFirmDto & { address?: CreateAddress } = {
        name: data.name,
        principalOrDirectorName: data?.principalOrDirectorName,
        companyNumber: data?.companyNumber,
        fcaNumber: data?.financialConductAuthorityNumber,
        icoNumber: data?.informationCommissionersOfficeNumber,
        hasHppPermissions: mapHasHppPermissionsToApi(data?.hasHPPPermissions),
        authorisationType: data?.authorisationType,
        website: data?.website,
        emailAddress: data?.email,
        phoneNumber: data?.phoneNumber,
        brokerIds: referrer ? [referrer.id] : [],
        address: data.address
          ? {
              addressLine1: data.address.addressLine1,
              addressLine2: data.address.addressLine2,
              postcode: data.address.postcode,
              town: data.address.town,
              country: data.address.country,
              county: data.address.county || null,
            }
          : undefined,
      };

      const { data: firm } = await API.put<ICreateFirmResponse>(`/monolith/referrers/firms`, firmData);

      return { firmId: firm.id, firmClerkId: firm.clerkId };
    } catch (err: any) {
      console.error(err);
      throw new Error(err.response.data.message);
    }
  },
};
